import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Car, CarFront, Truck, Loader2, CreditCard, ImagePlus, X } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { LEVELS } from "./Home";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const VEHICLES = [
  { id: "auto", label: "Auto", icon: Car },
  { id: "furgone", label: "Furgone", icon: CarFront },
  { id: "camion", label: "Camion", icon: Truck },
];

const inputCls =
  "w-full bg-obsidian/60 border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt transition-colors";

const errMsg = (e) =>
  typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Qualcosa è andato storto. Riprova.";

export default function NewTicket() {
  const { user, loading, authHeader } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    vehicle_type: "auto",
    vehicle_info: "",
    dtc_codes: "",
    description: "",
    level: LEVELS.some((l) => l.id === params.get("level")) ? params.get("level") : "avanzato",
  });
  const [photos, setPhotos] = useState([]); // {path, preview, name}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {ticket_id, price_eur}
  const [methods, setMethods] = useState({ sumup: false });

  useEffect(() => {
    api.get("/payments/methods").then((r) => setMethods(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const tid = params.get("ticket_id");
    if (tid && user) {
      api
        .get(`/tickets/${tid}`, { headers: authHeader() })
        .then((r) => {
          if (r.data.status === "pending_payment") setCreated({ ticket_id: r.data.ticket_id, price_eur: r.data.price_eur, level: r.data.level, title: r.data.title });
          else navigate(`/ticket/${tid}`);
        })
        .catch(() => toast.error("Ticket non trovato"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="text-volt animate-spin" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/accedi" replace />;
  if (user.role !== "cliente") {
    return (
      <div className="min-h-screen bg-obsidian">
        <Nav />
        <div className="pt-40 text-center px-4">
          <p className="text-slate-400" data-testid="new-ticket-wrong-role">Solo i clienti possono aprire ticket.</p>
        </div>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: authHeader() });
        setPhotos((p) => [...p, { path: data.path, preview: URL.createObjectURL(f), name: f.name }]);
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x.path !== path));

  const submit = async () => {
    if (form.title.trim().length < 5) return toast.error("Dai un titolo al problema (min. 5 caratteri)");
    if (form.description.trim().length < 20) return toast.error("Descrivi il problema più in dettaglio (min. 20 caratteri)");
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", { ...form, photos: photos.map((p) => p.path) }, { headers: authHeader() });
      setCreated({ ...data, level: form.level, title: form.title });
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/payments/tickets/checkout",
        { ticket_id: created.ticket_id, origin_url: window.location.origin },
        { headers: authHeader() }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  const levelInfo = LEVELS.find((l) => l.id === (created?.level || form.level));

  return (
    <div className="min-h-screen bg-obsidian" data-testid="new-ticket-page">
      <Nav />
      <main className="px-4 sm:px-8 max-w-3xl mx-auto pt-28 pb-20">
        {!created ? (
          <>
            <p className="eyebrow mb-2">Nuovo Ticket</p>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white mb-10">
              Descrivi il problema
            </h1>

            <div className="space-y-7">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Tipo di veicolo</p>
                <div className="grid grid-cols-3 gap-3" data-testid="ticket-vehicle-type">
                  {VEHICLES.map((v) => (
                    <button
                      key={v.id}
                      data-testid={`ticket-vtype-${v.id}`}
                      onClick={() => setForm((f) => ({ ...f, vehicle_type: v.id }))}
                      className={`rounded-xl border p-4 flex flex-col items-center gap-2 transition-all ${
                        form.vehicle_type === v.id ? "border-volt bg-volt/10 text-white" : "border-white/10 text-slate-500 hover:border-volt/40"
                      }`}
                    >
                      <v.icon size={22} className={form.vehicle_type === v.id ? "text-volt" : ""} />
                      <span className="text-xs font-mono uppercase tracking-wider">{v.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <input data-testid="ticket-title-input" className={inputCls} placeholder="Titolo (es. Spia motore accesa e perdita di potenza) *" value={form.title} onChange={set("title")} />
              <input data-testid="ticket-vehicle-input" className={inputCls} placeholder="Veicolo (es. Iveco Daily 2.3 2018)" value={form.vehicle_info} onChange={set("vehicle_info")} />
              <input data-testid="ticket-dtc-input" className={`${inputCls} font-mono uppercase`} placeholder="Codici errore DTC (es. P0300, U0423)" value={form.dtc_codes} onChange={set("dtc_codes")} />
              <textarea
                data-testid="ticket-description-input"
                className={`${inputCls} min-h-[130px] resize-y`}
                placeholder="Descrivi il problema in dettaglio: quando succede, cosa hai già provato, rumori, spie... *"
                value={form.description}
                onChange={set("description")}
              />

              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Foto (max 6)</p>
                <div className="flex flex-wrap gap-3" data-testid="ticket-photos">
                  {photos.map((p) => (
                    <div key={p.path} className="relative w-20 h-20 rounded-lg overflow-hidden border border-white/10">
                      <img src={p.preview} alt={p.name} className="w-full h-full object-cover" />
                      <button
                        data-testid={`ticket-photo-remove-${p.path.split("/").pop()}`}
                        onClick={() => removePhoto(p.path)}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-obsidian/80 text-slate-300 flex items-center justify-center hover:text-red-400"
                        aria-label="Rimuovi foto"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {photos.length < 6 && (
                    <label
                      data-testid="ticket-photo-upload"
                      className="w-20 h-20 rounded-lg border border-dashed border-white/20 flex flex-col items-center justify-center text-slate-500 hover:border-volt hover:text-volt cursor-pointer transition-colors"
                    >
                      {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                      <span className="text-[9px] font-mono mt-1 uppercase">Aggiungi</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={onFiles} />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Livello di consulenza</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ticket-level-select">
                  {LEVELS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`ticket-level-${l.id}`}
                      onClick={() => setForm((f) => ({ ...f, level: l.id }))}
                      className={`rounded-xl border p-4 text-left transition-all ${
                        form.level === l.id ? "border-volt bg-volt/10" : "border-white/10 hover:border-volt/40"
                      }`}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{l.label}</span>
                      <span className={`block font-display text-2xl font-extrabold mt-1 ${form.level === l.id ? "text-volt" : "text-white"}`}>
                        €{l.price}
                      </span>
                      <span className="block text-[11px] text-slate-500 mt-1">{l.tagline}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="ticket-submit-button"
                onClick={submit}
                disabled={busy || uploading}
                className="w-full flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold px-6 py-4 rounded-full hover:bg-spark hover:scale-[1.01] transition-all duration-200 disabled:opacity-50"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                Continua al pagamento — €{levelInfo.price}
              </button>
            </div>
          </>
        ) : (
          <div className="glass-card rounded-2xl p-6 sm:p-10 text-center" data-testid="ticket-payment-panel">
            <p className="eyebrow mb-4">Ultimo passo</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">Paga e pubblica il ticket</h1>
            <p className="mt-3 text-sm text-slate-400 max-w-md mx-auto">
              "{created.title}" — livello <strong className="text-white">{levelInfo.label}</strong>. Il ticket sarà
              visibile agli esperti appena il pagamento è confermato.
            </p>
            <div className="font-display text-5xl font-extrabold text-volt mt-6" data-testid="ticket-payment-price">
              €{created.price_eur}
            </div>

            <button
              data-testid="ticket-pay-card-button"
              onClick={payCard}
              disabled={busy}
              className="mt-8 w-full max-w-sm mx-auto flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold px-6 py-3.5 rounded-full hover:bg-spark transition-all duration-200 disabled:opacity-50"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              Paga con Carta
            </button>

            <div className="mt-4 max-w-sm mx-auto" data-testid="ticket-payment-paypal">
              {PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "black", shape: "pill", label: "pay" }}
                    createOrder={async () => {
                      const { data } = await api.post(
                        "/paypal/tickets/orders",
                        { ticket_id: created.ticket_id },
                        { headers: authHeader() }
                      );
                      return data.order_id;
                    }}
                    onApprove={async (d) => {
                      try {
                        await api.post(`/paypal/orders/${d.orderID}/capture`, {}, { headers: authHeader() });
                        toast.success("Pagamento confermato! Il ticket è ora visibile agli esperti.");
                        navigate("/dashboard");
                      } catch (err) {
                        toast.error(errMsg(err));
                      }
                    }}
                    onError={() => toast.error("Pagamento PayPal non riuscito")}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="rounded-xl border border-dashed border-white/15 px-4 py-3">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">PayPal · in attivazione</span>
                </div>
              )}
            </div>

            {methods.sumup && (
              <button
                data-testid="ticket-pay-sumup-button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data } = await api.post(
                      "/payments/tickets/sumup",
                      { ticket_id: created.ticket_id, origin_url: window.location.origin },
                      { headers: authHeader() }
                    );
                    window.location.href = data.hosted_checkout_url;
                  } catch (err) {
                    toast.error(errMsg(err));
                    setBusy(false);
                  }
                }}
                className="mt-3 w-full max-w-sm mx-auto flex items-center justify-center gap-2 border border-white/20 text-slate-200 font-semibold px-6 py-3.5 rounded-full hover:border-volt hover:text-volt transition-all duration-200 disabled:opacity-50"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                Paga con SumUp
              </button>
            )}

            <button
              data-testid="ticket-pay-later-button"
              onClick={() => navigate("/dashboard")}
              className="mt-5 font-mono text-[11px] uppercase tracking-widest text-slate-500 hover:text-volt transition-colors"
            >
              Paga più tardi dalla dashboard
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
